import * as React from 'react'
import classNames from 'classnames'

type AlignIndicator = 'left' | 'right'

export interface CheckboxProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
    alignIndicator?: AlignIndicator
    indeterminate?: boolean
    inline?: boolean
    label?: React.ReactNode
    large?: boolean
}

const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
    (
        {
            alignIndicator = 'left',
            checked,
            className,
            disabled,
            indeterminate = false,
            inline = false,
            label,
            large = false,
            onChange,
            ...props
        },
        ref,
    ) => {
        const inputRef = React.useRef<HTMLInputElement | null>(null)
        const isControlled = checked !== undefined
        const [uncontrolledChecked, setUncontrolledChecked] = React.useState(
            Boolean(props.defaultChecked),
        )
        const currentChecked = isControlled ? Boolean(checked) : uncontrolledChecked
        const isActive = currentChecked || indeterminate
        const hasVisibleLabel = label != null

        React.useEffect(() => {
            if (inputRef.current) {
                inputRef.current.indeterminate = indeterminate
            }
        }, [indeterminate])

        const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
            if (!isControlled) {
                setUncontrolledChecked(event.target.checked)
            }
            onChange?.(event)
        }

        const setRefs = (node: HTMLInputElement | null) => {
            inputRef.current = node
            if (typeof ref === 'function') {
                ref(node)
            } else if (ref) {
                ref.current = node
            }
        }

        const indicator = (
            <span
                aria-hidden="true"
                className={classNames(
                    'relative inline-flex flex-none items-center justify-center rounded-[2px] transition-colors',
                    large ? 'h-[20px] w-[20px]' : 'h-[16px] w-[16px]',
                    isActive
                        ? 'bg-[#2d72d2] shadow-[inset_0_0_0_1px_rgba(17,20,24,0.2)] group-hover:bg-[#215db0] group-active:bg-[#184a90]'
                        : 'bg-transparent shadow-[inset_0_0_0_1px_#C1CDD6] group-hover:bg-[rgba(143,153,168,0.15)] group-active:bg-[rgba(143,153,168,0.3)]',
                    disabled &&
                        (isActive
                            ? 'cursor-not-allowed bg-[rgba(45,114,210,0.5)] shadow-none group-hover:bg-[rgba(45,114,210,0.5)] group-active:bg-[rgba(45,114,210,0.5)]'
                            : 'cursor-not-allowed bg-[rgba(143,153,168,0.15)] shadow-[inset_0_0_0_1px_rgba(95,107,124,0.45)] group-hover:bg-[rgba(143,153,168,0.15)] group-active:bg-[rgba(143,153,168,0.15)]'),
                )}
            >
                {indeterminate ? (
                    <span
                        className={classNames(
                            'h-[2px] rounded-sm bg-white',
                            large ? 'w-[12px]' : 'w-[10px]',
                        )}
                    />
                ) : currentChecked ? (
                    <svg
                        className={classNames(large ? 'h-[20px] w-[20px]' : 'h-[16px] w-[16px]')}
                        viewBox="0 0 16 16"
                        aria-hidden="true"
                    >
                        <path
                            fill="white"
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M12 5c-.28 0-.53.11-.71.29L7 9.59l-2.29-2.3a1.003 1.003 0 00-1.42 1.42l3 3c.18.18.43.29.71.29s.53-.11.71-.29l5-5A1.003 1.003 0 0012 5z"
                        />
                    </svg>
                ) : null}
            </span>
        )

        return (
            <label
                className={classNames(
                    'group cursor-pointer select-none items-center text-sm font-normal normal-case text-[#1c2127]',
                    inline ? 'inline-flex mr-[20px]' : 'flex mb-[10px]',
                    disabled && 'cursor-not-allowed text-[rgba(95,107,124,0.6)]',
                    className,
                )}
            >
                <input
                    {...props}
                    ref={setRefs}
                    type="checkbox"
                    checked={checked}
                    disabled={disabled}
                    onChange={handleChange}
                    className="sr-only"
                />
                {alignIndicator === 'left' && indicator}
                {hasVisibleLabel && (
                    <span
                        className={classNames(
                            alignIndicator === 'left' ? 'ml-[10px]' : 'mr-[10px]',
                        )}
                    >
                        {label}
                    </span>
                )}
                {alignIndicator === 'right' && indicator}
            </label>
        )
    },
)
Checkbox.displayName = 'Checkbox'

export { Checkbox }
